import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { accountApi, authApi } from "../api";
import { organizationApi } from "../api/organization/organizationApi";
import { notificationsApi } from "@/api/notifications/notificationsApi.js";
import { setAuthToken, setLogoutFunction, getApiErrorMessage } from "../utils/axios";
import { stopStaffRealtimeHubConnection } from "../utils/realtimeHub";
import { toast } from "sonner";
import {
  decodeToken,
  getClaim,
  getPermissionsFromToken,
  getRolesFromToken,
  isTokenExpired,
} from "../utils/jwtUtils";

const AuthContext = createContext(null);
const ORG_SWITCH_TOAST_KEY = "hotel:orgSwitchToast";

const consumeOrgSwitchToast = () => {
  try {
    const raw = sessionStorage.getItem(ORG_SWITCH_TOAST_KEY);
    if (!raw) return;
    sessionStorage.removeItem(ORG_SWITCH_TOAST_KEY);
    const payload = JSON.parse(raw);
    if (payload?.type === "success" && payload?.message) {
      toast.success(payload.message);
    } else if (payload?.message) {
      toast.message(payload.message);
    }
  } catch {
    sessionStorage.removeItem(ORG_SWITCH_TOAST_KEY);
  }
};

const normalizeAuthResponse = (response) => {
  const data = response?.data?.data || response?.data || response;

  return {
    token:
      data?.token ||
      data?.Token ||
      data?.accessToken ||
      data?.AccessToken ||
      null,

    refreshToken:
      data?.refreshToken ||
      data?.RefreshToken ||
      null,

    userDetails:
      data?.userDetails ||
      data?.UserDetails ||
      null,
  };
};

const getStoredUserDetails = () => {
  try {
    const savedUser = localStorage.getItem("userDetails");
    return savedUser ? JSON.parse(savedUser) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const logoutInProgressRef = useRef(false);

  const [accessToken, setAccessToken] = useState(() =>
    localStorage.getItem("accessToken")
  );

  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem("refreshToken")
  );

  const [userDetails, setUserDetails] = useState(getStoredUserDetails);

  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [userFirstName, setUserFirstName] = useState("");
  const [profileUrl, setProfileUrl] = useState("");

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState("");

  const [organizationId, setOrganizationId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationCode, setOrganizationCode] = useState("");
  const [organizationType, setOrganizationType] = useState("");
  const [orgImageUrl, setOrgImageUrl] = useState("");
  const [appLogo, setAppLogo] = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [isSetupCompleted, setIsSetupCompleted] = useState(false);
  const [canUseHotel, setCanUseHotel] = useState(null);
  const [canUsePos, setCanUsePos] = useState(null);
  const [canUseBookings, setCanUseBookings] = useState(null);
  const [canUseCalls, setCanUseCalls] = useState(null);
  const [canUseGuestPortal, setCanUseGuestPortal] = useState(null);
  const [canUseChat, setCanUseChat] = useState(null);
  const [canUseGuestCrm, setCanUseGuestCrm] = useState(null);
  const [canUseLoyalty, setCanUseLoyalty] = useState(null);
  const [canUseSurveys, setCanUseSurveys] = useState(null);
  const [canUseBilling, setCanUseBilling] = useState(null);

  const [loading, setLoading] = useState(true);

  const clearAuthState = useCallback(() => {
    stopStaffRealtimeHubConnection().catch(() => { });

    setAccessToken(null);
    setRefreshToken(null);
    setUserDetails(null);

    setUserId("");
    setEmail("");
    setUserFirstName("");
    setProfileUrl("");

    setRoles([]);
    setPermissions([]);
    setUserRole("");

    setOrganizationId("");
    setOrganizationName("");
    setOrganizationCode("");
    setOrganizationType("");
    setOrgImageUrl("");
    setAppLogo("");
    setBackgroundImage("");
    setIsSetupCompleted(false);
    setCanUseHotel(null);
    setCanUsePos(null);
    setCanUseBookings(null);
    setCanUseCalls(null);
    setCanUseGuestPortal(null);
    setCanUseChat(null);
    setCanUseGuestCrm(null);
    setCanUseLoyalty(null);
    setCanUseSurveys(null);
    setCanUseBilling(null);

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userDetails");

    setAuthToken(null);
  }, []);

  const deactivateCurrentPushSubscription = useCallback(async (accessToken) => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    const token = accessToken || localStorage.getItem("accessToken");
    const registration = await navigator.serviceWorker.ready.catch(() => null);
    const subscription = await registration?.pushManager?.getSubscription?.();
    if (!subscription?.endpoint) {
      return;
    }

    await notificationsApi
      .unsubscribe(
        { endpoint: subscription.endpoint },
        token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined,
      )
      .catch((error) => {
        console.warn("[push] backend unsubscribe during logout failed", error);
      });

    await subscription.unsubscribe().catch((error) => {
      console.warn("[push] browser unsubscribe during logout failed", error);
    });
  }, []);

  const logout = useCallback(() => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    const accessToken = localStorage.getItem("accessToken");
    deactivateCurrentPushSubscription(accessToken).catch(() => { });

    clearAuthState();
    navigate("/login", { replace: true });

    logoutInProgressRef.current = false;
  }, [clearAuthState, deactivateCurrentPushSubscription, navigate]);

  useEffect(() => {
    setLogoutFunction(logout);
  }, [logout]);

  const hydrateFromToken = useCallback((token, userFromResponse = null) => {
    const decoded = decodeToken(token);

    if (!decoded || isTokenExpired(decoded)) {
      return false;
    }

    const tokenRoles = getRolesFromToken(decoded);
    const tokenPermissions = getPermissionsFromToken(decoded);

    const tokenUserId = getClaim(decoded, [
      "nameid",
      "sub",
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier",
    ]);

    const tokenEmail = getClaim(decoded, [
      "email",
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
    ]);

    setUserId(tokenUserId);
    setEmail(tokenEmail);

    setUserFirstName(
      getClaim(decoded, ["UserFirstName", "userFirstName"])
    );

    setProfileUrl(
      getClaim(decoded, ["ProfileUrl", "profileUrl"])
    );

    setRoles(tokenRoles);
    setPermissions(tokenPermissions);
    setUserRole(tokenRoles?.[0] || "");

    setOrganizationId(
      getClaim(decoded, ["OrganizationId", "organizationId"])
    );

    setOrganizationName(
      getClaim(decoded, ["OrganizationName", "organizationName"])
    );

    setOrganizationCode(
      getClaim(decoded, ["OrganizationCode", "organizationCode"])
    );

    setOrganizationType(
      getClaim(decoded, ["OrganizationType", "organizationType"])
    );

    setOrgImageUrl(
      getClaim(decoded, ["ImageUrl", "imageUrl"])
    );

    setAppLogo(
      getClaim(decoded, ["AppLogo", "appLogo"])
    );

    setBackgroundImage(
      getClaim(decoded, ["BackgroundImage", "backgroundImage"])
    );

    const setupCompleted = getClaim(decoded, [
      "IsSetupCompleted",
      "isSetupCompleted",
    ]);

    setIsSetupCompleted(
      setupCompleted === true ||
      setupCompleted === "True" ||
      setupCompleted === "true"
    );

    const parseFlag = (value) => {
      if (value === true || value === "True" || value === "true") return true;
      if (value === false || value === "False" || value === "false") return false;
      return null;
    };

    setCanUseHotel(
      parseFlag(getClaim(decoded, ["CanUseHotel", "canUseHotel"]))
    );
    setCanUsePos(parseFlag(getClaim(decoded, ["CanUsePos", "canUsePos"])));
    setCanUseBookings(parseFlag(getClaim(decoded, ["CanUseBookings", "canUseBookings"])));
    setCanUseCalls(parseFlag(getClaim(decoded, ["CanUseCalls", "canUseCalls"])));
    setCanUseGuestPortal(parseFlag(getClaim(decoded, ["CanUseGuestPortal", "canUseGuestPortal"])));
    setCanUseChat(parseFlag(getClaim(decoded, ["CanUseChat", "canUseChat"])));
    setCanUseGuestCrm(parseFlag(getClaim(decoded, ["CanUseGuestCrm", "canUseGuestCrm"])));
    setCanUseLoyalty(parseFlag(getClaim(decoded, ["CanUseLoyalty", "canUseLoyalty"])));
    setCanUseSurveys(parseFlag(getClaim(decoded, ["CanUseSurveys", "canUseSurveys"])));
    setCanUseBilling(parseFlag(getClaim(decoded, ["CanUseBilling", "canUseBilling"])));

    const detailPermissions =
      userFromResponse?.permissions || userFromResponse?.Permissions;

    if (Array.isArray(detailPermissions) && detailPermissions.length > 0) {
      setPermissions(detailPermissions);
    }

    const detailRoles =
      userFromResponse?.roleNames ||
      userFromResponse?.RoleNames ||
      userFromResponse?.roles ||
      userFromResponse?.Roles;

    if (Array.isArray(detailRoles) && detailRoles.length > 0) {
      setRoles(detailRoles);
      setUserRole(detailRoles[0] || "");
    }

    if (userFromResponse) {
      setUserDetails(userFromResponse);
      localStorage.setItem("userDetails", JSON.stringify(userFromResponse));
    }

    return true;
  }, []);

  const applyCurrentUserPayload = useCallback((currentUser) => {
    if (!currentUser) {
      return;
    }

    if (Array.isArray(currentUser.permissions) && currentUser.permissions.length > 0) {
      setPermissions(currentUser.permissions);
    }

    if (Array.isArray(currentUser.roles) && currentUser.roles.length > 0) {
      setRoles(currentUser.roles);
      setUserRole(currentUser.roles[0] || "");
    }

    if (currentUser.id) {
      setUserId(String(currentUser.id));
    }

    if (currentUser.email) {
      setEmail(currentUser.email);
    }

    if (currentUser.userName) {
      setUserFirstName(currentUser.userName);
    }

    if (currentUser.organizationId) {
      setOrganizationId(String(currentUser.organizationId));
    }

    if (currentUser.organizationName) {
      setOrganizationName(currentUser.organizationName);
    }
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    try {
      const response = await accountApi.getCurrent();
      const currentUser = response?.data?.data || response?.data;
      applyCurrentUserPayload(currentUser);
      return { success: true, data: currentUser };
    } catch (error) {
      console.error("Failed to refresh current user", error);
      return { success: false };
    }
  }, [applyCurrentUserPayload]);

  const saveAuthData = useCallback(
    (authData) => {
      if (!authData?.token) {
        return false;
      }

      const isValid = hydrateFromToken(authData.token, authData.userDetails);

      if (!isValid) {
        clearAuthState();
        return false;
      }

      setAccessToken(authData.token);
      localStorage.setItem("accessToken", authData.token);
      setAuthToken(authData.token);

      if (authData.refreshToken) {
        setRefreshToken(authData.refreshToken);
        localStorage.setItem("refreshToken", authData.refreshToken);
      }

      if (authData.userDetails) {
        setUserDetails(authData.userDetails);
        localStorage.setItem(
          "userDetails",
          JSON.stringify(authData.userDetails)
        );
      }

      return true;
    },
    [clearAuthState, hydrateFromToken]
  );

  const login = useCallback(
    async (payload) => {
      try {
        const response = await authApi.login(payload);
        const authData = normalizeAuthResponse(response);

        const success = saveAuthData(authData);

        if (!success) {
          return {
            success: false,
            message: "Invalid login response.",
          };
        }

        await refreshCurrentUser();
        navigate("/hotel/dashboard", { replace: true });

        return {
          success: true,
          data: authData,
        };
      } catch (error) {
        console.error("Login failed", error);

        return {
          success: false,
          message: getApiErrorMessage(error, "Invalid email or password. Please try again."),
        };
      }
    },
    [navigate, refreshCurrentUser, saveAuthData]
  );

  const register = useCallback(
    async (payload) => {
      try {
        const response = await authApi.register(payload);
        const authData = normalizeAuthResponse(response);

        if (authData.token) {
          saveAuthData(authData);
          navigate("/hotel/dashboard", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }

        return {
          success: true,
          data: response?.data,
        };
      } catch (error) {
        console.error("Register failed", error);

        return {
          success: false,
          message: getApiErrorMessage(error, "Registration failed. Please try again."),
        };
      }
    },
    [navigate, saveAuthData]
  );

  /**
   * Switches the active organization without logging the user out.
   * - Calls POST /api/Organization/switch-organization
   * - Receives a fresh JWT scoped to the target org
   * - Re-hydrates all auth state in place
   * - Navigates to /hotel/dashboard to force a clean page reload
   */
  const switchOrganization = useCallback(
    async (organizationId) => {
      try {
        const response = await organizationApi.switchOrganization(
          organizationId,
          "hotel",
        );
        const authData = normalizeAuthResponse(response);

        const success = saveAuthData(authData);
        if (!success) {
          return { success: false, message: "Failed to switch organization." };
        }

        await stopStaffRealtimeHubConnection().catch(() => { });

        const switchedName = getClaim(decodeToken(authData.token), [
          "OrganizationName",
          "organizationName",
        ]);
        try {
          sessionStorage.setItem(
            ORG_SWITCH_TOAST_KEY,
            JSON.stringify({
              type: "success",
              message: switchedName
                ? `Switched to ${switchedName}`
                : "Organization switched",
            }),
          );
        } catch {
          // Ignore storage failures; reload still proceeds.
        }
        window.location.assign("/hotel/dashboard");

        return { success: true };
      } catch (error) {
        console.error("switchOrganization failed", error);
        return {
          success: false,
          message:
            error?.response?.data?.message ||
            error?.response?.data?.Message ||
            "Failed to switch organization.",
        };
      }
    },
    [saveAuthData]
  );

  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      setLoading(true);

      const token = localStorage.getItem("accessToken");

      if (!token) {
        clearAuthState();
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      const decoded = decodeToken(token);

      if (!decoded || isTokenExpired(decoded)) {
        clearAuthState();
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      setAuthToken(token);
      hydrateFromToken(token, getStoredUserDetails());
      await refreshCurrentUser();

      if (!cancelled) {
        setLoading(false);
        consumeOrgSwitchToast();
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [clearAuthState, hydrateFromToken, refreshCurrentUser]);

  const isAuthenticated = !!accessToken;

  const organizationCapabilities = useMemo(() => {
    const hotel = canUseHotel !== false;
    const pos = canUsePos !== false;
    const bookings = canUseBookings !== null ? canUseBookings : hotel;
    const calls = canUseCalls !== null ? canUseCalls : (hotel || pos);
    const guestPortal = canUseGuestPortal !== null ? canUseGuestPortal : hotel;
    const chat = canUseChat !== null ? canUseChat : (hotel || pos);
    const guestCrm = canUseGuestCrm !== null ? canUseGuestCrm : (hotel || pos);
    const loyalty = canUseLoyalty !== null ? canUseLoyalty : (hotel || pos);
    const surveys = canUseSurveys !== null ? canUseSurveys : (hotel || pos);
    const guestBilling = canUseBilling !== null ? canUseBilling : (hotel || pos);

    const type = (organizationType || "").toLowerCase().trim();

    if (type === "food & beverages" && canUseHotel === null) {
      return {
        hotel: false,
        rooms: false,
        pos: true,
        bookings: false,
        calls: true,
        guestPortal: false,
        chat: true,
        guestCrm: true,
        loyalty: true,
        surveys: true,
        guestBilling: true,
      };
    }

    return {
      hotel,
      rooms: hotel,
      pos,
      bookings,
      calls,
      guestPortal,
      chat,
      guestCrm,
      loyalty,
      surveys,
      guestBilling,
    };
  }, [
    canUseHotel,
    canUsePos,
    canUseBookings,
    canUseCalls,
    canUseGuestPortal,
    canUseChat,
    canUseGuestCrm,
    canUseLoyalty,
    canUseSurveys,
    canUseBilling,
    organizationType,
  ]);

  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true;
      if (
        roles.some((role) =>
          ["Admin", "Superadmin", "Super Admin"].includes(role)
        )
      ) {
        return true;
      }
      return permissions.includes(permission);
    },
    [permissions, roles]
  );

  const hasAnyPermission = useCallback(
    (required) => {
      if (!required?.length) return true;
      return required.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  const hasScoringAccess = useMemo(() => {
    const isUserAdmin = roles.some((r) =>
      ["admin", "super admin", "superadmin", "manager"].includes(String(r).toLowerCase().trim())
    ) || String(userRole || "").toLowerCase().includes("admin");

    const hasScoringPerm = permissions.some((p) =>
      String(p).toLowerCase().startsWith("scoring.")
    );

    return isUserAdmin || hasScoringPerm;
  }, [roles, userRole, permissions]);

  return (
    <AuthContext.Provider
      value={{
        loading,

        accessToken,
        refreshToken,
        isAuthenticated,

        login,
        register,
        logout,
        refreshCurrentUser,
        switchOrganization,

        userDetails,
        userId,
        email,
        userFirstName,
        profileUrl,

        roles,
        permissions,
        userRole,
        hasPermission,
        hasAnyPermission,
        hasScoringAccess,

        organizationId,
        organizationName,
        organizationCode,
        organizationType,
        organizationCapabilities,
        orgImageUrl,
        appLogo,
        backgroundImage,
        isSetupCompleted,

        setAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
